import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Checkbox, FormControlLabel, FormGroup, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { TextField } from "../../../TextField/TextField.styled";
import { BriefingFormModel } from "pages/BriefingForm/interfaces";
import { Controller } from "react-hook-form";
import { MAX_BESPOKE_LENGTH, TRU_SKIN, TRU_SKIN_IDS } from "validation/components/BriefingForm/briefingFormRules";

const OTHER_ID = "Other";

const options = [
    {
        id: "Half_Page_300x600",
        label: "briefingForm.label.halfPage",
    },
    {
        id: "Large_Mobile_Banner_320x100",
        label: "briefingForm.label.largeMobileBanner",
    },
    {
        id: "Large_Rectangle_336x280",
        label: "briefingForm.label.largeRectangle",
    },
    {
        id: OTHER_ID,
        label: "briefingForm.label.other",
    },
];

const useStyles = makeStyles({
    root: {
        alignItems: "flex-start",
    },
});

interface BespokeProps extends FieldsHookForm<BriefingFormModel> {
    selectedIds: string[];
    // eslint-disable-next-line
    onAdSizeSelect(onChange: (...event: any[]) => void, clickedId: string | string[]): void;
    onResetOtherDescriptionValue(): void;
    isMaxSelected(selectedIds: string[], id: string, maxLength: number): boolean;
}

const disableTruSkin = (selectedIds: string[]) =>
    selectedIds.length > 1 && !selectedIds.find((id) => id.includes(TRU_SKIN));

export const Bespoke: React.FC<BespokeProps> = ({
    control,
    selectedIds,
    onAdSizeSelect,
    onResetOtherDescriptionValue,
    isMaxSelected,
}) => {
    const { t } = useTranslation();
    const [otherSelected, setOtherSelected] = useState<boolean>(selectedIds.includes(OTHER_ID));
    const classes = useStyles();

    const handleOtherOptionsDescription = (isSelected: boolean) => {
        if (!isSelected) {
            onResetOtherDescriptionValue();
        }

        setOtherSelected(isSelected);
    };

    // eslint-disable-next-line
    const onSizeSelect = (onChange: (...event: any[]) => void, id: string) => {
        if (id === OTHER_ID) {
            handleOtherOptionsDescription(!selectedIds.includes(id));
        }

        onAdSizeSelect(onChange, id);
    };

    return (
        <Box>
            <Typography variant="subtitle2">{t("briefingForm.label.upto5Sizes")}</Typography>
            <Box display="flex">
                <FormGroup>
                    {options.map(({ id, label }) => (
                        <Controller
                            key={id}
                            control={control}
                            type="checkbox"
                            name="adSize.sizes"
                            render={({ onChange }) => (
                                <FormControlLabel
                                    onChange={() => onSizeSelect(onChange, id)}
                                    label={t(label)}
                                    control={<Checkbox name={id} color="primary" />}
                                    checked={selectedIds.includes(id)}
                                    disabled={isMaxSelected(selectedIds, id, MAX_BESPOKE_LENGTH)}
                                />
                            )}
                        />
                    ))}
                </FormGroup>
                <FormGroup>
                    <Controller
                        control={control}
                        name="adSize.sizes"
                        render={({ onChange }) => {
                            return (
                                <FormControlLabel
                                    className={classes.root}
                                    label={
                                        <Box mt="10px">
                                            <Typography variant="body1">{t("briefingForm.label.truSkin")}</Typography>
                                            <Typography variant="subtitle2">
                                                {t("briefingForm.label.includes")}
                                            </Typography>
                                            <Typography variant="subtitle2">{t("briefingForm.label.ts1")}</Typography>
                                            <Typography variant="subtitle2">{t("briefingForm.label.ts2")}</Typography>
                                            <Typography variant="subtitle2">{t("briefingForm.label.ts3")}</Typography>
                                            <Typography variant="subtitle2">{t("briefingForm.label.ts4")}</Typography>
                                        </Box>
                                    }
                                    disabled={disableTruSkin(selectedIds)}
                                    checked={!!selectedIds.find((id) => id.includes(TRU_SKIN))}
                                    onChange={() => onAdSizeSelect(onChange, TRU_SKIN_IDS)}
                                    control={<Checkbox name={TRU_SKIN} color="primary" />}
                                />
                            );
                        }}
                    />
                </FormGroup>
            </Box>
            {otherSelected && (
                <Controller
                    as={
                        <TextField
                            label={t("briefingForm.label.otherPlaceholder")}
                            multiline
                            rows={5}
                            variant="outlined"
                            placeholder={t("briefingForm.label.otherExample")}
                            fullWidth
                        />
                    }
                    name="adSize.otherDescription"
                    control={control}
                />
            )}
        </Box>
    );
};
